/**
 * Vídeo comprável: mute, indicador, setas do carrossel e autoplay do fundo.
 */
class JvShoppableVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-jvs-video]');
    this.bgVideo = this.querySelector('[data-jvs-bg-video]');
    this.muteBtn = this.querySelector('[data-jvs-mute]');
    this.track = this.querySelector('[data-jvs-track]');
    this.indicators = this.querySelectorAll('[data-jvs-indicator]');
    this.indicatorFills = this.querySelectorAll('[data-jvs-indicator-fill]');
    this.prevBtn = this.querySelector('[data-jvs-prev]');
    this.nextBtn = this.querySelector('[data-jvs-next]');

    this._onMuteClick = (event) => this.#toggleMute(event);
    this._onScroll = () => this.#syncChrome();
    this._onResize = () => this.#syncChrome();
    this._onPrev = () => this.#scrollBy(-1);
    this._onNext = () => this.#scrollBy(1);

    this.#ensurePlay(this.video);
    this.#ensurePlay(this.bgVideo);

    if (this.muteBtn && this.video) {
      this.muteBtn.addEventListener('click', this._onMuteClick);
      this.#syncMuteUi();
    }

    if (this.track) {
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      this.#syncChrome();
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
  }

  disconnectedCallback() {
    if (this.muteBtn && this._onMuteClick) {
      this.muteBtn.removeEventListener('click', this._onMuteClick);
    }
    if (this.track && this._onScroll) {
      this.track.removeEventListener('scroll', this._onScroll);
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    if (this.prevBtn && this._onPrev) this.prevBtn.removeEventListener('click', this._onPrev);
    if (this.nextBtn && this._onNext) this.nextBtn.removeEventListener('click', this._onNext);
  }

  #ensurePlay(video) {
    if (!video) return;
    video.muted = true;
    video.setAttribute('muted', '');
    const play = () => {
      video.play().catch(() => {});
    };
    if (video.readyState >= 2) {
      play();
    } else {
      video.addEventListener('loadeddata', play, { once: true });
    }
  }

  #toggleMute(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.video) return;

    const nextMuted = !this.video.muted;
    this.video.muted = nextMuted;
    if (nextMuted) {
      this.video.setAttribute('muted', '');
    } else {
      this.video.removeAttribute('muted');
      if (this.video.volume === 0) this.video.volume = 1;
      this.video.play().catch(() => {});
    }
    this.#syncMuteUi();
  }

  #syncMuteUi() {
    if (!this.muteBtn || !this.video) return;
    const muted = this.video.muted;
    this.muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
    this.muteBtn.setAttribute(
      'aria-label',
      muted ? this.muteBtn.dataset.labelUnmute || '' : this.muteBtn.dataset.labelMute || ''
    );
    const mutedIcon = this.muteBtn.querySelector('[data-icon="muted"]');
    const unmutedIcon = this.muteBtn.querySelector('[data-icon="unmuted"]');
    if (mutedIcon) mutedIcon.toggleAttribute('hidden', !muted);
    if (unmutedIcon) unmutedIcon.toggleAttribute('hidden', muted);
  }

  #slideStep() {
    if (!this.track) return 0;
    const slide = this.track.querySelector('.jvs-v__slide');
    if (!slide) return this.track.clientWidth;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  #scrollBy(direction) {
    if (!this.track) return;
    const step = this.#slideStep();
    if (step <= 0) return;
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  #syncChrome() {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const scrollLeft = this.track.scrollLeft;
    const ratio = max > 1 ? scrollLeft / max : 0;
    const scale = Math.min(1, Math.max(0, ratio || 0.08));
    const hide = max <= 1;

    this.indicatorFills.forEach((fill) => {
      fill.style.transform = `scaleX(${scale})`;
    });
    this.indicators.forEach((indicator) => {
      indicator.hidden = hide;
    });

    if (this.prevBtn) this.prevBtn.disabled = scrollLeft <= 1;
    if (this.nextBtn) this.nextBtn.disabled = max <= 1 || scrollLeft >= max - 1;
  }
}

if (!customElements.get('jv-shoppable-video')) {
  customElements.define('jv-shoppable-video', JvShoppableVideo);
}
